import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { EmptyState } from '../common/EmptyState';
import { TableSkeleton } from '../common/Skeleton';

export function TransferRuleList() {
  const { confirm } = useConfirm();
  const utils = trpc.useUtils();
  const rulesQuery = trpc.transferRule.list.useQuery();
  const createMutation = trpc.transferRule.create.useMutation({
    onSuccess: (data) => {
      void utils.transferRule.list.invalidate();
      void utils.insights.overview.invalidate();
      void utils.transaction.list.invalidate();
      setAdding(false);
      const msg =
        data.flaggedCount > 0
          ? `Överföringsregel skapad — ${data.flaggedCount} transaktion${data.flaggedCount === 1 ? '' : 'er'} markerad${data.flaggedCount === 1 ? '' : 'e'} som överföring`
          : 'Överföringsregel skapad';
      toast.success(msg);
    },
    onError: (err) => toast.error(err.message),
  });
  const deleteMutation = trpc.transferRule.delete.useMutation({
    onSuccess: (data) => {
      void utils.transferRule.list.invalidate();
      void utils.insights.overview.invalidate();
      void utils.transaction.list.invalidate();
      const msg =
        data.unflaggedCount > 0
          ? `Överföringsregel borttagen — ${data.unflaggedCount} transaktion${data.unflaggedCount === 1 ? '' : 'er'} avmarkerad${data.unflaggedCount === 1 ? '' : 'e'}`
          : 'Överföringsregel borttagen';
      toast.success(msg);
    },
    onError: (err) => toast.error(err.message),
  });

  const [adding, setAdding] = useState(false);
  const [newPattern, setNewPattern] = useState('');
  const [newMatchType, setNewMatchType] = useState<'contains' | 'exact' | 'starts_with'>(
    'contains',
  );
  const [newLabel, setNewLabel] = useState('');
  const [debouncedPattern, setDebouncedPattern] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPattern(newPattern.trim()), 300);
    return () => clearTimeout(timer);
  }, [newPattern]);

  const previewQuery = trpc.transferRule.preview.useQuery(
    { pattern: debouncedPattern, matchType: newMatchType },
    { enabled: adding && debouncedPattern.length >= 2 },
  );

  const resetForm = () => {
    setNewPattern('');
    setNewLabel('');
    setNewMatchType('contains');
    setDebouncedPattern('');
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPattern.trim()) return;
    createMutation.mutate({
      pattern: newPattern.trim(),
      matchType: newMatchType,
      label: newLabel.trim() || null,
    });
  };

  const handleClose = () => {
    setAdding(false);
    resetForm();
  };

  const rules = rulesQuery.data ?? [];

  if (rulesQuery.isLoading) return <TableSkeleton rows={4} columns={4} />;

  const matchTypeLabels: Record<string, string> = {
    contains: 'Innehåller',
    exact: 'Exakt',
    // biome-ignore lint/style/useNamingConvention: matches DB enum value
    starts_with: 'Börjar med',
  };

  const renderDeleteButton = (rule: { id: string; pattern: string }) => (
    <Button
      type="button"
      onClick={async () => {
        const ok = await confirm({
          title: 'Ta bort överföringsregel',
          message: `Är du säker på att du vill ta bort regeln "${rule.pattern}"?`,
          confirmLabel: 'Ta bort',
          destructive: true,
        });
        if (ok)
          deleteMutation.mutate({
            id: rule.id,
          });
      }}
      disabled={deleteMutation.isPending}
      variant="ghost"
      size="sm"
      aria-label={`Ta bort regeln ${rule.pattern}`}
      className="h-11 shrink-0 px-3 text-xs text-destructive hover:text-destructive/80 sm:h-auto sm:px-2"
    >
      Ta bort
    </Button>
  );

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div>
            <CardTitle className="text-[15px] font-semibold">Överföringsregler</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Transaktioner som matchar en överföringsregel markeras automatiskt som interna
              överföringar och exkluderas från budgetsammanställningar.
            </p>
          </div>
          <Button type="button" onClick={() => setAdding(true)}>
            + Lägg till regel
          </Button>
        </CardHeader>

        {/* Rules list */}
        <CardContent>
          {rules.length === 0 ? (
            <EmptyState
              icon="🔀"
              title="Inga överföringsregler"
              description="Skapa regler för att automatiskt markera interna överföringar."
              action={{
                label: '+ Lägg till regel',
                onClick: () => setAdding(true),
              }}
            />
          ) : (
            <>
              {/* Mobile: stacked rows */}
              <ul className="divide-y divide-border text-sm md:hidden">
                {rules.map((rule) => (
                  <li key={rule.id} className="flex items-center gap-2 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="break-all font-mono text-foreground">{rule.pattern}</div>
                      <div className="text-xs text-muted-foreground">
                        {matchTypeLabels[rule.matchType] ?? rule.matchType}
                        {rule.label ? ` · ${rule.label}` : ''}
                      </div>
                    </div>
                    {renderDeleteButton(rule)}
                  </li>
                ))}
              </ul>

              {/* Desktop: table */}
              <table className="hidden w-full text-sm md:table">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="py-1.5 text-left font-medium">Mönster</th>
                    <th className="py-1.5 px-2 text-left font-medium">Typ</th>
                    <th className="py-1.5 px-2 text-left font-medium">Etikett</th>
                    <th className="py-1.5 text-right font-medium">
                      <span className="sr-only">Åtgärder</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr key={rule.id} className="border-b border-border last:border-0">
                      <td className="break-words py-2 text-foreground font-mono">{rule.pattern}</td>
                      <td className="py-2 px-2 text-muted-foreground">
                        {matchTypeLabels[rule.matchType] ?? rule.matchType}
                      </td>
                      <td className="py-2 px-2 text-muted-foreground">{rule.label || '—'}</td>
                      <td className="py-2 text-right">{renderDeleteButton(rule)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardContent>
      </Card>

      {adding && (
        <Dialog open onOpenChange={(open) => !open && handleClose()}>
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>Lägg till överföringsregel</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <Label htmlFor="transfer-pattern" className="text-xs text-muted-foreground">
                  Mönster
                </Label>
                <Input
                  id="transfer-pattern"
                  type="text"
                  required
                  value={newPattern}
                  onChange={(e) => setNewPattern(e.target.value)}
                  placeholder="t.ex. Överföring 960213"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="transfer-match-type" className="text-xs text-muted-foreground">
                  Matchningstyp
                </Label>
                <Select
                  value={newMatchType}
                  onValueChange={(value) =>
                    setNewMatchType(value as 'contains' | 'exact' | 'starts_with')
                  }
                >
                  <SelectTrigger id="transfer-match-type" className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">Innehåller</SelectItem>
                    <SelectItem value="exact">Exakt</SelectItem>
                    <SelectItem value="starts_with">Börjar med</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="transfer-label" className="text-xs text-muted-foreground">
                  Etikett (valfritt)
                </Label>
                <Input
                  id="transfer-label"
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="t.ex. Sparkonto"
                  className="mt-1"
                />
              </div>

              {previewQuery.data && debouncedPattern.length >= 2 && (
                <div className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                  <div className="flex flex-wrap gap-3">
                    <span>
                      Totalt matchande:{' '}
                      <strong className="text-foreground">{previewQuery.data.totalMatching}</strong>
                    </span>
                    {previewQuery.data.willFlag > 0 && (
                      <span>
                        Kommer markeras:{' '}
                        <strong className="text-foreground">{previewQuery.data.willFlag}</strong>
                      </span>
                    )}
                    {previewQuery.data.alreadyFlagged > 0 && (
                      <span>
                        Redan markerade:{' '}
                        <strong className="text-positive">
                          {previewQuery.data.alreadyFlagged}
                        </strong>
                      </span>
                    )}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Avbryt
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending
                    ? 'Lägger till…'
                    : previewQuery.data?.willFlag
                      ? `Lägg till & markera ${previewQuery.data.willFlag} st`
                      : 'Lägg till'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
